const STORAGE_KEY = 'pa.debug.canonical';

function getGlobal() {
  if (typeof globalThis !== 'undefined') return globalThis;
  if (typeof window !== 'undefined') return window;
  return {};
}

export function isCanonicalDebugEnabled() {
  const g = getGlobal();
  if (typeof g.__PA_CANONICAL_DEBUG_ENABLED__ === 'boolean') {
    return g.__PA_CANONICAL_DEBUG_ENABLED__;
  }
  try {
    const value = g.localStorage?.getItem(STORAGE_KEY);
    if (value === 'false' || value === '0' || value === 'off') return false;
    if (value === 'true' || value === '1' || value === 'on') return true;
  } catch (_) {
    // localStorage can be unavailable in embedded/platform contexts.
  }
  // This Sprint 1 diagnostic build is intentionally on by default.
  return true;
}

export function setCanonicalDebugEnabled(enabled) {
  const value = Boolean(enabled);
  const g = getGlobal();
  g.__PA_CANONICAL_DEBUG_ENABLED__ = value;
  try { g.localStorage?.setItem(STORAGE_KEY, value ? 'true' : 'false'); } catch (_) {}
  return value;
}

function caseRow(item = {}) {
  return {
    id: item.id,
    name: item.name,
    nativeId: item.source?.nativeId || '',
    key: item.key || '',
    attributes: item.attributes,
    extensions: item.extensions,
  };
}

function eventRow(item = {}) {
  return {
    id: item.id,
    caseId: item.caseId,
    activity: item.activity,
    activityType: item.activityType,
    timestamp: item.timestamp,
    resource: item.resource,
    nativeRecordId: item.source?.nativeRecordId || '',
  };
}

export function canonicalDebugSummary(dataset, metrics = null) {
  return {
    schemaVersion: dataset?.schemaVersion || '',
    platform: dataset?.source?.platform || '',
    stage: dataset?.source?.stage || '',
    modelId: dataset?.model?.id || '',
    modelName: dataset?.model?.name || '',
    caseCount: Array.isArray(dataset?.cases) ? dataset.cases.length : 0,
    eventCount: Array.isArray(dataset?.events) ? dataset.events.length : 0,
    caseAttributeCount: Array.isArray(dataset?.attributeDefinitions?.case) ? dataset.attributeDefinitions.case.length : 0,
    eventAttributeCount: Array.isArray(dataset?.attributeDefinitions?.event) ? dataset.attributeDefinitions.event.length : 0,
    casesWithComments: (dataset?.cases || []).filter(c => (c?.extensions?.engagement?.commentCount || 0) > 0).length,
    casesWithWorklogs: (dataset?.cases || []).filter(c => (c?.extensions?.engagement?.worklogCount || 0) > 0).length,
    casesWithSla: (dataset?.cases || []).filter(c => c?.extensions?.sla?.available === true).length,
    persistence: dataset?.metadata?.persistence || null,
    metrics,
  };
}

/**
 * Browser-console diagnostics for the canonical process dataset.
 *
 * Exposes the latest dataset at window.__PA_CANONICAL__ so it can be inspected
 * without breakpoints. The object intentionally references the live dataset.
 */
export function debugCanonicalDataset(dataset, {
  label = 'Canonical dataset',
  metrics = null,
  sampleSize = 10,
  expose = true,
  stage = '',
  persistence = null,
} = {}) {
  if (!dataset || !isCanonicalDebugEnabled()) return dataset;

  const g = getGlobal();
  const logger = g.console;
  const summary = canonicalDebugSummary(dataset, metrics);
  if (stage) summary.debugStage = stage;
  if (persistence) summary.debugPersistence = persistence;

  if (expose) {
    const api = {
      dataset,
      cases: dataset.cases || [],
      events: dataset.events || [],
      metrics,
      summary,
      persistence: summary.persistence || persistence || null,
      caseByKey(key) { return (dataset.cases || []).find(c => String(c.key) === String(key)) || null; },
      caseById(id) { return (dataset.cases || []).find(c => String(c.id) === String(id)) || null; },
      snapshot() {
        try { return JSON.parse(JSON.stringify(dataset)); }
        catch (_) { return dataset; }
      },
      enable() { return setCanonicalDebugEnabled(true); },
      disable() { return setCanonicalDebugEnabled(false); },
    };
    g.__PA_CANONICAL__ = api;
    g.__PA_CANONICAL_DATASET__ = dataset;
    g.__PA_CANONICAL_METRICS__ = metrics;
  }

  if (!logger) return dataset;
  const title = `[PA Canonical] ${label} — ${summary.caseCount} cases / ${summary.eventCount} events`;
  const group = logger.groupCollapsed || logger.group;

  try {
    if (typeof group === 'function') group.call(logger, title);
    else logger.log(title);

    logger.log('Summary', summary);
    logger.log('Source', dataset.source);
    logger.log('Model', dataset.model);
    if (summary.persistence || persistence) logger.log('Persistence', summary.persistence || persistence);
    logger.log('Attribute definitions', dataset.attributeDefinitions);
    if (metrics) logger.log('Canonical metrics', metrics);
    logger.log('FULL canonical dataset (expand this object)', dataset);

    const cases = (dataset.cases || []).slice(0, sampleSize).map(caseRow);
    const events = (dataset.events || []).slice(0, sampleSize).map(eventRow);
    if (typeof logger.table === 'function') {
      logger.table(cases);
      logger.table(events);
    } else {
      logger.log(`First ${cases.length} cases`, cases);
      logger.log(`First ${events.length} events`, events);
    }

    logger.log('Console shortcut: __PA_CANONICAL__.dataset');
    logger.log('Cases: __PA_CANONICAL__.cases');
    logger.log('Inspect case by key: __PA_CANONICAL__.caseByKey("ABC-123")');
    logger.log('Events: __PA_CANONICAL__.events');
    logger.log('Metrics: __PA_CANONICAL__.metrics');
    logger.log('Persistence: __PA_CANONICAL__.persistence');
    logger.log('Detached JSON copy: __PA_CANONICAL__.snapshot()');
  } catch (_) {
    // Debugging must never affect process execution.
  } finally {
    try { if (typeof logger.groupEnd === 'function') logger.groupEnd(); } catch (_) {}
  }

  return dataset;
}
